import React, { FC, useEffect, useState } from 'react'
import cn from 'classnames'
import ReactTooltip from 'react-tooltip'
import '../../../assets/stylesheets/design-catalog.css'
import Button from '../components/Button'
import QuantityStepper from '../components/QuantityStepper'
import Search from '../components/Search'
import Select from '../components/Select'
import Toast from '../components/Toast'
import Tooltip from '../components/Tooltip'
import ProductPhoto from '../components/ProductPhoto'
import CrossIcon from '../icons/cross.svg'
import TrashIcon from '../icons/trash.svg'
import ChevronIcon from '../icons/chevron_down_round.svg'
import RepackedIcon from '../icons/repacked.svg'
import BoxIcon from '../icons/box.svg'
import CartPlusIcon from '../icons/cart_plus.svg'
import CartMinusIcon from '../icons/cart_minus.svg'
import TransferArrowsIcon from '../icons/transfer_arrows.svg'
import EditSquareIcon from '../icons/edit_square.svg'
import MoveToIcon from '../icons/move_to.svg'
import MoveOutIcon from '../icons/move_out.svg'
import SigmaSquareIcon from '../icons/sigma_square.svg'
import SquareArrowUpRightIcon from '../icons/square_arrow_up_right.svg'

const COLORS = [
  'primary-text',
  'grey-again',
  'secondary-text',
  'additional-text',
  'denim',
  'primary-green',
  'primary-red',
  'asap-orange',
  'light-orange',
  'lines-grey',
  'black-squeeze',
  'dark-text',
  'icon-grey',
  'tint-blue',
]

const TYPOGRAPHY = [
  { name: 'Page title · 19 Medium', className: '-title' },
  { name: 'Card title · 16 Medium', className: '-card-title' },
  { name: 'Amount · 17 Medium', className: '-amount' },
  { name: 'Body · 14 Regular', className: '-body' },
  { name: 'Label · 13 Regular', className: '-label' },
  { name: 'Meta · 12 Regular', className: '-meta' },
]

const OPTIONS = ['Option one', 'Option two', 'Option three']

const ROWS = [
  { title: 'List item title', hint: 'info' },
  {
    title: 'A longer list item title that wraps onto a second line',
    hint: 'warning',
  },
  { title: 'List item title', hint: 'alert' },
]

const ALL = 'All'
const SCOPES = ['All items', 'Scope one', 'Scope two']
const CHIPS = [ALL, 'Option one', 'Option two', 'Option three', 'Option four']

const ICONS = [
  { name: 'box', Icon: BoxIcon },
  { name: 'cart_plus', Icon: CartPlusIcon },
  { name: 'cart_minus', Icon: CartMinusIcon },
  { name: 'transfer_arrows', Icon: TransferArrowsIcon },
  { name: 'edit_square', Icon: EditSquareIcon },
  { name: 'move_to', Icon: MoveToIcon },
  { name: 'move_out', Icon: MoveOutIcon },
  { name: 'sigma_square', Icon: SigmaSquareIcon },
  { name: 'square_arrow_up_right', Icon: SquareArrowUpRightIcon },
]

const EVENTS = [
  {
    type: 'Option one',
    Icon: BoxIcon,
    delta: '+500',
    total: '1,000',
    link: true,
  },
  {
    type: 'Option two',
    Icon: CartMinusIcon,
    delta: '-250',
    total: '500',
    sections: ['Section one', 'Section two'],
  },
  {
    type: 'Option five',
    Icon: CartPlusIcon,
    delta: '+250',
    total: '750',
    mirrored: true,
  },
  {
    type: 'Option three',
    Icon: TransferArrowsIcon,
    delta: '-50',
    total: '750',
    meta: 'A long secondary line that is cut with an ellipsis when it does not fit',
  },
  { type: 'Option four', Icon: SigmaSquareIcon, delta: '0', total: '800' },
  { type: 'Option six', Icon: MoveOutIcon, delta: '-100', total: '800' },
]

const deltaClass = (delta: string) =>
  cn('history-row_delta', {
    '-positive': delta.startsWith('+'),
    '-negative': delta.startsWith('-'),
  })

const Section: FC<{
  title: string
  note?: string
  children: React.ReactNode
}> = ({ title, note, children }) => (
  <section className="design-catalog_section">
    <h2 className="design-catalog_title">{title}</h2>
    {note && <p className="design-catalog_note">{note}</p>}
    {children}
  </section>
)

const Sample: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="design-catalog_sample">
    <div className="design-catalog_label">{label}</div>
    {children}
  </div>
)

const Hint: FC<{ kind: string }> = ({ kind }) => {
  if (kind === 'alert') return <div className="cart-stock -alert">ALERT</div>
  return (
    <div className={cn('cart-stock', kind === 'info' ? '-in' : '-low')}>
      <strong>128</strong> {kind === 'info' ? 'info text.' : 'warning text.'}
    </div>
  )
}

const DesignCatalog: FC = () => {
  const [amounts, setAmounts] = useState<string[]>(ROWS.map(() => '1'))
  const [option, setOption] = useState(OPTIONS[0])
  const [chips, setChips] = useState<string[]>([])
  const [scope, setScope] = useState(SCOPES[0])

  useEffect(() => {
    ReactTooltip.rebuild()
  }, [chips])

  const toggleChip = (c: string) =>
    setChips((prev) =>
      c === ALL
        ? []
        : prev.includes(c)
          ? prev.filter((p) => p !== c)
          : [...prev, c],
    )

  const setAmount = (index: number, value: string) =>
    setAmounts((prev) => prev.map((v, i) => (i === index ? value : v)))

  return (
    <div className="design-catalog">
      <header className="design-catalog_head">
        <h1>Design catalog</h1>
        <p>Reference elements for new screens.</p>
      </header>

      <Section
        title="Colors"
        note="CSS custom properties from common/setup.css"
      >
        <div className="design-catalog_swatches">
          {COLORS.map((color) => (
            <div key={color} className="design-catalog_swatch">
              <span style={{ background: `var(--${color})` }} />
              --{color}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Typography" note="Roboto 400 / 500 / 600">
        {TYPOGRAPHY.map((type) => (
          <div
            key={type.name}
            className={`design-catalog_type ${type.className}`}
          >
            {type.name}
          </div>
        ))}
      </Section>

      <Section title="Buttons" note="Button + .cart-button">
        <div className="design-catalog_row">
          <Button color="primary" className="cart-button">
            Primary
          </Button>
          <Button color="success" className="cart-button">
            Success
          </Button>
          <Button color="danger" className="cart-button">
            Danger
          </Button>
          <Button color="success" className="cart-button" disabled>
            Disabled
          </Button>
        </div>
      </Section>

      <Section
        title="Controls"
        note="QuantityStepper, dropdown (.cart-unit), Select (react-select)"
      >
        <div className="design-catalog_row">
          <Sample label="Stepper">
            <QuantityStepper
              value={amounts[0]}
              canDecrement={+amounts[0] > 1}
              onChange={(v) => setAmount(0, v)}
              onDecrement={() =>
                setAmount(0, String(Math.max(1, +amounts[0] - 1)))
              }
              onIncrement={() => setAmount(0, String(+amounts[0] + 1))}
            />
          </Sample>
          <Sample label="Stepper · disabled">
            <QuantityStepper
              value="1"
              disabled
              onChange={() => undefined}
              onDecrement={() => undefined}
              onIncrement={() => undefined}
            />
          </Sample>
          <Sample label="Dropdown · open">
            <div className="cart-unit -select">
              {option}
              <ChevronIcon className="cart-unit_chevron" />
            </div>
            <ul className="cart-unit_menu">
              {OPTIONS.map((o) => (
                <li
                  key={o}
                  className={cn('cart-unit_option', {
                    '-selected': o === option,
                  })}
                  onClick={() => setOption(o)}
                >
                  {o}
                </li>
              ))}
            </ul>
          </Sample>
          <Sample label="Select">
            <div className="design-catalog_field">
              <Select
                label="Label"
                defaultValue={{ label: OPTIONS[0], value: OPTIONS[0] }}
                options={OPTIONS.map((o) => ({ label: o, value: o }))}
              />
            </div>
          </Sample>
        </div>
      </Section>

      <Section title="Hints" note=".cart-stock, icon label with tooltip">
        <div className="design-catalog_row">
          <Sample label="Accent · info">
            <Hint kind="info" />
          </Sample>
          <Sample label="Accent · warning">
            <Hint kind="warning" />
          </Sample>
          <Sample label="Alert">
            <Hint kind="alert" />
          </Sample>
          <Sample label="Icon label · hover">
            <div className="cart-row_meta">
              <Tooltip id="catalog-tooltip" className="cart-tooltip">
                <span
                  className="cart-on-hand -repacked"
                  data-tip="Tooltip title<br/><br/>Tooltip text"
                  data-for="catalog-tooltip"
                  data-effect="solid"
                  data-place="top"
                >
                  <RepackedIcon className="cart-on-hand_icon" />
                  Icon label
                </span>
              </Tooltip>
            </div>
          </Sample>
        </div>
      </Section>

      <Section title="Header" note=".cart_header">
        <div className="design-catalog_frame">
          <div className="cart_header">
            <div className="cart_header-info">
              <div className="cart_branch">Section label</div>
              <div className="cart_title">
                Page title - <span className="cart_po">Action link</span>
              </div>
            </div>
            <button type="button" className="cart_close clear-button">
              <CrossIcon width={15} height={16} />
            </button>
          </div>
        </div>
      </Section>

      <Section title="Alert" note=".cart_alert">
        <div className="cart_alert design-catalog_flush">
          Alert message text
        </div>
      </Section>

      <Section
        title="List"
        note=".cart_card with toolbar, notice section (.cart-unavailable) and rows (.cart-row)"
      >
        <div className="cart_card design-catalog_list">
          <div className="cart_toolbar">
            <div className="cart_search">
              <Search placeholder="Search" />
            </div>
            <div className="cart_summary">
              {ROWS.length} items ·{' '}
              <button type="button" className="cart_clear clear-button">
                Clear
              </button>
            </div>
          </div>
          <div className="cart-unavailable">
            <div className="cart-unavailable_info">
              <div className="cart-unavailable_title">Notice title</div>
              <div className="cart-unavailable_text">
                Notice text explaining the state.
                <br />
                Secondary line with an{' '}
                <span className="cart-unavailable_link">inline link</span>.
              </div>
            </div>
            <Button color="danger" className="cart-button">
              Danger
            </Button>
            <Button color="primary" className="cart-button">
              Primary
            </Button>
          </div>
          {ROWS.map((row, index) => (
            <div key={index} className="cart-row">
              <div className="cart-row_photo">
                <ProductPhoto className="cart-row_image" />
              </div>
              <div className="cart-row_info">
                <div
                  className={cn('cart-row_name', {
                    '-unavailable': row.hint === 'alert',
                  })}
                >
                  <span>{row.title}</span>
                </div>
                <div className="cart-row_meta">Secondary · Meta text</div>
              </div>
              <div className="cart-row_amount">
                <div className="cart-row_controls">
                  <QuantityStepper
                    value={amounts[index]}
                    disabled={row.hint === 'alert'}
                    canDecrement={+amounts[index] > 1}
                    onChange={(v) => setAmount(index, v)}
                    onDecrement={() =>
                      setAmount(index, String(Math.max(1, +amounts[index] - 1)))
                    }
                    onIncrement={() =>
                      setAmount(index, String(+amounts[index] + 1))
                    }
                  />
                  <div className="cart-unit">Value / unit</div>
                </div>
                <Hint kind={row.hint} />
              </div>
              <div className="cart-row_total">
                <div className="cart-row_sum">$100.00</div>
                <button type="button" className="cart-row_remove clear-button">
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Panels"
        note=".cart-panel (-tinted), .cart-ship, .cart-total"
      >
        <div className="design-catalog_sidebar">
          <div className="cart-panel -tinted">
            <div className="cart-panel_title">Panel title</div>
            <div className="cart-panel_stack">
              <div>
                <div className="cart-panel_headline">Headline</div>
                <div className="cart-panel_subtitle">Subtitle text</div>
              </div>
              <Button color="primary" block className="cart-button">
                Primary
              </Button>
            </div>
            <div className="cart-panel_divider" />
            <div className="cart-panel_note">* Footnote text</div>
          </div>
          <div className="cart-panel">
            <div className="cart-panel_title">Panel title</div>
            <div className="cart-ship">
              <span className="cart-ship_radio" />
              <div className="cart-ship_text">
                <div className="cart-ship_name">Option label</div>
                <div className="cart-ship_address">Option description</div>
              </div>
            </div>
          </div>
          <div className="cart-panel">
            <div className="cart-panel_title">Panel title</div>
            <div className="cart-total">
              <div className="cart-total_row">
                <span>Label</span>
                <span className="cart-total_value">Value</span>
              </div>
              <div className="cart-total_row">
                <span>Label</span>
                <span className="cart-total_value">Value</span>
              </div>
              <div className="cart-panel_divider" />
              <div className="cart-total_row">
                <span>Total</span>
                <span className="cart-total_value -sum">$100.00</span>
              </div>
            </div>
            <div className="cart-total_actions">
              <Button color="success" block className="cart-button">
                Success
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Icons" note="src/icons, 16px, stroke uses currentColor">
        <div className="design-catalog_row">
          {ICONS.map(({ name, Icon }) => (
            <Sample key={name} label={name}>
              <Icon />
            </Sample>
          ))}
        </div>
      </Section>

      <Section
        title="Filterable list"
        note={
          '.history-location (Select), .history-chips (All resets, others OR), ' +
          '.history-row (.-highlighted), .history-row_icon.-mirrored, click .history-details (one section per location)'
        }
      >
        <div className="design-catalog_panel">
          <div className="history-location">
            <Select
              value={{ label: scope, value: scope }}
              options={SCOPES.map((s) => ({ label: s, value: s }))}
              onChange={(o) => o && setScope(o.value)}
            />
          </div>
          <div className="history-chips">
            {CHIPS.map((c) => (
              <button
                key={c}
                type="button"
                className={cn('history-chip clear-button', {
                  '-active': c === ALL ? !chips.length : chips.includes(c),
                })}
                onClick={() => toggleChip(c)}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="history-list">
            {EVENTS.filter(
              ({ type }) => !chips.length || chips.includes(type),
            ).map(({ type, Icon, delta, total, link, meta, mirrored }) => (
              <div
                key={type}
                className="history-row"
                data-tip={type}
                data-for="catalog-history"
              >
                <div
                  className={cn('history-row_icon', { '-mirrored': mirrored })}
                >
                  <Icon />
                </div>
                <div className="history-row_info">
                  <div className="history-row_line">
                    <span className="history-row_text">{type} · Jun 10</span>
                    <span className={deltaClass(delta)}>{delta}</span>
                  </div>
                  <div className="history-row_line -meta">
                    {link ? (
                      <span className="history-row_link">
                        <SquareArrowUpRightIcon />
                        Link text
                      </span>
                    ) : (
                      <span className="history-row_text">
                        {meta || 'Secondary text'}
                      </span>
                    )}
                    <span>{total}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <ReactTooltip
            id="catalog-history"
            className="history-tooltip"
            place="bottom"
            effect="solid"
            event="click"
            globalEventOff="click"
            isCapture
            getContent={(type) => {
              const event = EVENTS.find((e) => e.type === type)
              return (
                event && (
                  <div className="history-details">
                    <div className="history-details_row">
                      Label
                      <strong>Value</strong>
                    </div>
                    <div className="history-details_row">
                      Label
                      <strong>Value</strong>
                    </div>
                    {(event.sections || [undefined]).map((section, i) => (
                      <React.Fragment key={i}>
                        <div className="history-details_divider" />
                        {section && (
                          <div className="history-details_row">{section}</div>
                        )}
                        <div className="history-details_row">
                          Label
                          <strong>{event.delta}</strong>
                        </div>
                        <div className="history-details_row">
                          Label
                          <strong>{event.total}</strong>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )
              )
            }}
          />
        </div>
      </Section>

      <Section title="Toast" note="Toast">
        <div className="design-catalog_toast">
          <Toast
            title="Toast title"
            text="Supporting text"
            onUndo={() => undefined}
            onClose={() => undefined}
          />
        </div>
      </Section>
    </div>
  )
}

export default DesignCatalog
